import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, MapPin, Building2, Tag, CheckCircle2, Clock, Calendar, FileText, PhoneCall, MailOpen, Activity, X, Trash2, Edit2, Send } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { getStatusColors } from './../../../utils/statusUtils';
import { useAuth } from '../../../context/AuthContext';
import { formatAppDate, formatAppDateOnly } from '../../../utils/dateUtils';
import Dropdown from '../../../components/Dropdown';

const LeadProfile = ({ lead, onClose, onEditLead, onDeleteLead, onActionLead }) => {
  const { user: currentUser, hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('activity');
  const [localFollowups, setLocalFollowups] = useState(lead?.followups || []);
  const [visits, setVisits] = useState([]);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [checkInNote, setCheckInNote] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [checkInLat, setCheckInLat] = useState('');
  const [checkInLng, setCheckInLng] = useState('');
  const [checkOutLat, setCheckOutLat] = useState('');
  const [checkOutLng, setCheckOutLng] = useState('');

  const canEditOrDelete = currentUser?.role === 'Admin' || lead?.createdBy === currentUser?.name || lead?.assignee === currentUser?.name;

  useEffect(() => {
    setLocalFollowups(lead?.followups || []);
  }, [lead?.followups]);

  useEffect(() => {
    const fetchVisits = async () => {
      try {
        const { default: api } = await import('../../../utils/api');
        const visitsRes = await api.get(`/visits/lead/${lead.id}`);
        setVisits(visitsRes.data || []);
      } catch (e) {
        console.log('Failed to fetch visits');
      }
    };
    if (lead?.id) fetchVisits();
  }, [lead?.id]);
  
  const handleCheckInClick = () => {
    setShowCheckInModal(true);
    setCheckInNote('');
    setCheckInLat('');
    setCheckInLng('');
  };

  const handleCheckIn = async () => {
    try {
      const { default: api } = await import('../../../utils/api');
      const res = await api.post('/visits/checkin', { 
        leadId: lead.id, 
        notes: checkInNote
      });
      setVisits([res.data, ...visits]);
      setShowCheckInModal(false);

      // Re-fetch lead or update local followups to show visit in activity timeline immediately
      const updatedLead = await api.get(`/leads/${lead.id}`);
      if (updatedLead.data && updatedLead.data.followups) {
        setLocalFollowups(updatedLead.data.followups);
      }
    } catch (e) {}
  };
  
  const handleCheckOutClick = () => {
    setShowCheckOutModal(true);
    setCheckOutTime('');
    setCheckOutLat('');
    setCheckOutLng('');
  };

  const handleCheckOut = async () => {
    if (!activeVisit) return;
    try {
      const { default: api } = await import('../../../utils/api');
      const res = await api.put(`/visits/checkout/${activeVisit.id}`, { 
        checkOutTime
      });
      setVisits(visits.map(v => v.id === res.data.id ? res.data : v));
      setShowCheckOutModal(false);
      setCheckOutTime('');

      // Re-fetch followups to show checkout in activity timeline
      const updatedLead = await api.get(`/leads/${lead.id}`);
      if (updatedLead.data && updatedLead.data.followups) {
        setLocalFollowups(updatedLead.data.followups);
      }
    } catch (e) {}
  };
  
  const activeVisit = visits.find(v => !v.checkOut);


  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return formatAppDate(dateString);
    } catch {
      return dateString;
    }
  };

  const [deleteFollowupId, setDeleteFollowupId] = useState(null);
  const [editFollowupData, setEditFollowupData] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editDate, setEditDate] = useState(null);
  const [editTime, setEditTime] = useState('');
  const [editReason, setEditReason] = useState('Client Requested');

  const confirmDelete = async () => {
    if (!deleteFollowupId) return;
    try {
      const { default: api } = await import('../../../utils/api');
      const deletedEvent = localFollowups.find(f => f.id === deleteFollowupId);
      
      // 1. Delete the follow-up
      await api.delete(`/followups/${deleteFollowupId}`);
      
      // 2. Add system note for deletion
      const newFollowup = await api.post('/followups', {
        leadId: lead.id,
        date: new Date().toISOString(),
        notes: `[System] Activity deleted`,
        type: 'System'
      });
      
      let remaining = localFollowups.filter(f => f.id !== deleteFollowupId);
      remaining.unshift(newFollowup.data);
      
      // 3. Find previous status
      const statusRes = await api.get('/masters/status');
      const statuses = statusRes.data;
      
      let previousStatusName = 'New'; // default to New instead of Schedule
      const sorted = [...remaining].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      
      for (const f of sorted) {
        if (f.notes?.startsWith('[Status Change:')) {
          const match = f.notes.match(/^\[Status Change:\s*(.*?)\]/);
          if (match && match[1]) {
            previousStatusName = match[1].trim();
            break;
          }
        } else if (f.notes?.includes('[Status Change] Lead marked as')) {
          const match = f.notes.match(/Lead marked as (.*?)(?:\.|\n|$)/);
          if (match && match[1]) {
            previousStatusName = match[1].trim();
            break;
          }
        } else if (f.notes?.match(/^\[(.*?)\]/)) {
          const m = f.notes.match(/^\[(.*?)\]/)[1];
          if (['Complete', 'Completed', 'Closed', 'Lost', 'Schedule', 'Reschedule'].includes(m)) {
            previousStatusName = m.replace('ed', ''); // rough mapping
            break;
          }
        }
      }
      
      if (previousStatusName === 'Reschedule') previousStatusName = 'Schedule';
      if (previousStatusName === 'Complete') previousStatusName = 'Completed';
      
      const targetStatus = statuses.find(s => s.name.toLowerCase() === previousStatusName.toLowerCase() || s.name.toLowerCase().includes(previousStatusName.toLowerCase()));
      
      if (targetStatus) {
        await api.put(`/leads/${lead.id}`, { 
          statusId: targetStatus.id,
          isCustomer: ['won', 'completed', 'complete'].includes(targetStatus.name.toLowerCase())
        });
      }
      
      setLocalFollowups(remaining);
      import('react-hot-toast').then(({ toast }) => toast.success("Activity deleted and status reverted"));
      setDeleteFollowupId(null);
    } catch (err) {
      console.error("Failed to delete followup", err);
      import('react-hot-toast').then(({ toast }) => toast.error("Failed to delete follow-up"));
      setDeleteFollowupId(null);
    }
  };

  const confirmEdit = async () => {
    if (!editFollowupData) return;
    try {
      const { default: api } = await import('../../../utils/api');
      
      let combinedDateTime;
      try {
        const y = editDate.getFullYear();
        const m = String(editDate.getMonth() + 1).padStart(2, '0');
        const d = String(editDate.getDate()).padStart(2, '0');
        const dateStr = `${y}-${m}-${d}`;
        
        let timeVal = editTime;
        if (timeVal.toLowerCase().includes('pm')) {
          let parts = timeVal.toLowerCase().replace('pm','').trim().split(':');
          let h = parseInt(parts[0], 10);
          if (h !== 12) h += 12;
          timeVal = `${h.toString().padStart(2, '0')}:${parts[1] || '00'}`;
        } else if (timeVal.toLowerCase().includes('am')) {
          let parts = timeVal.toLowerCase().replace('am','').trim().split(':');
          let h = parseInt(parts[0], 10);
          if (h === 12) h = 0;
          timeVal = `${h.toString().padStart(2, '0')}:${parts[1] || '00'}`;
        }
        
        combinedDateTime = new Date(`${dateStr}T${timeVal}:00`);
        if (isNaN(combinedDateTime.getTime())) throw new Error("Invalid time");
      } catch (e) {
        combinedDateTime = editDate;
      }

      let newNotes = editNotes ? editNotes : '';
      let originalPrefixMatch = editFollowupData.notes?.match(/^\[(.*?)\]/);
      
      if (originalPrefixMatch) {
        const isTrueTerminal = ['Complete', 'Completed', 'Closed', 'Lost'].includes(originalPrefixMatch[1]);
        if (isTrueTerminal) {
          const prefix = originalPrefixMatch[1].endsWith('-Edited') ? originalPrefixMatch[1] : `${originalPrefixMatch[1]}-Edited`;
          newNotes = `[${prefix}] ${editNotes ? editNotes : ''}`;
        } else {
          newNotes = `[${originalPrefixMatch[1]}] ${editNotes ? editNotes : ''}`;
        }
      } else {
        newNotes = editNotes ? editNotes : 'Follow-up scheduled';
      }
      
      await api.put(`/followups/${editFollowupData.id}`, { 
        notes: newNotes,
        date: combinedDateTime.toISOString()
      });
      
      setLocalFollowups(prev => prev.map(item => item.id === editFollowupData.id ? { ...item, notes: newNotes, date: combinedDateTime.toISOString(), updatedAt: new Date().toISOString() } : item));
      
      if (onActionLead) onActionLead();
      import('react-hot-toast').then(({ toast }) => toast.success("Follow-up updated"));
      setEditFollowupData(null);
    } catch (err) {
      console.error("Failed to update", err);
      import('react-hot-toast').then(({ toast }) => toast.error("Failed to update"));
    }
  };

  return (
    <>
      <style>{`
        .lead-profile-header {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 1rem;
          padding: 1.5rem;
          background-color: white;
          border-bottom: 1px solid var(--color-border);
          flex-shrink: 0;
        }
        .lead-profile-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
          overflow-x: auto;
          padding-bottom: 0.5rem;
        }
        .lead-profile-actions::-webkit-scrollbar {
          height: 4px;
        }
        .lead-profile-actions::-webkit-scrollbar-thumb {
          background-color: #cbd5e1;
          border-radius: 4px;
        }
        @media (min-width: 1024px) {
          .lead-profile-header {
            flex-direction: row;
            align-items: center;
            padding: 1.5rem 2rem;
          }
          .lead-profile-actions {
            width: auto;
            overflow-x: visible;
            padding-bottom: 0;
          }
        }
      `}</style>
      <div style={{ width: '100%', height: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
        <div className="bg-white border-b z-10 w-full" style={{ borderColor: 'var(--color-border)' }}>
          {/* Header */}
          <div className="lead-profile-header" style={{ borderBottom: 'none' }}>
            <div className="flex items-center gap-4 w-full">
              <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', fontWeight: 'bold', flexShrink: 0 }}>
                {lead?.name?.charAt(0) || 'L'}
              </div>
              <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <h2 className="text-xl md:text-2xl font-bold" style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {lead?.name || 'Unknown Lead'}
                </h2>
                <p className="text-muted text-sm mt-1" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {lead?.machine?.name || (typeof lead?.machine === 'string' ? lead?.machine : null) || 'No machine specified'} • Added {lead?.createdAt ? formatDate(lead.createdAt) : 'Recently'}
                </p>
              </div>
            </div>
            <div className="lead-profile-actions">

              {hasPermission('leads:write') && onEditLead && !['complete', 'completed', 'not interested', 'closed', 'won', 'lost'].includes((lead.status?.name || lead.status)?.trim().toLowerCase()) && (
                <button className="btn whitespace-nowrap" style={{ backgroundColor: 'white', border: '1px solid var(--color-border)', color: 'var(--color-text-main)', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', fontSize: '0.95rem', fontWeight: '600' }} onClick={() => onEditLead(lead)}>
                  <Edit2 size={16} /> <span>Edit</span>
                </button>
              )}
              {hasPermission('leads:delete') && onDeleteLead && !['complete', 'completed', 'not interested', 'closed', 'won', 'lost'].includes((lead.status?.name || lead.status)?.trim().toLowerCase()) && (
                <button className="btn whitespace-nowrap" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', fontSize: '0.95rem', fontWeight: '600' }} onClick={() => onDeleteLead(lead.id)}>
                  <Trash2 size={16} /> <span>Delete</span>
                </button>
              )}
              <button className="btn whitespace-nowrap" style={{ backgroundColor: 'white', border: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', fontSize: '0.95rem', fontWeight: '600' }} onClick={onClose}>
                <X size={16} /> <span>Close</span>
              </button>
            </div>
          </div>
          
          <div className="flex justify-between items-end hide-scrollbar overflow-x-auto w-full px-6 lg:px-8">
            <div className="flex gap-8">
              <button 
                className={`pb-3 font-semibold transition-colors whitespace-nowrap ${activeTab === 'details' ? 'border-b-2' : 'text-muted hover:text-main'}`}
                style={{ borderColor: activeTab === 'details' ? 'var(--color-primary)' : 'transparent', color: activeTab === 'details' ? 'var(--color-primary)' : undefined, fontSize: '16px', marginBottom: '-1px' }}
                onClick={() => setActiveTab('details')}
              >
                Lead Details
              </button>
              <button 
                className={`pb-3 font-semibold transition-colors whitespace-nowrap ${activeTab === 'activity' ? 'border-b-2' : 'text-muted hover:text-main'}`}
                style={{ borderColor: activeTab === 'activity' ? 'var(--color-primary)' : 'transparent', color: activeTab === 'activity' ? 'var(--color-primary)' : undefined, fontSize: '16px', marginBottom: '-1px' }}
                onClick={() => setActiveTab('activity')}
              >
                Activity Timeline
              </button>
            </div>
            
            <div className="flex items-center mb-3 mt-1">
              {onActionLead && !['complete', 'completed', 'not interested', 'closed', 'won', 'lost'].includes((lead.status?.name || lead.status)?.trim().toLowerCase()) && hasPermission('followups:write') && (
                <button className="btn whitespace-nowrap" style={{ backgroundColor: 'white', border: '1px solid var(--color-border)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.25rem', fontSize: '0.95rem', fontWeight: '600' }} onClick={() => onActionLead(lead)}>
                  <Activity size={18} /> <span>Action</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="hide-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '2rem', backgroundColor: '#f8fafc' }}>
          {activeTab === 'details' ? (() => {
            let latVal = lead?.latitude;
            let lngVal = lead?.longitude;
            const cleanAddress1 = lead?.address ? lead.address.replace(/\s*\[GPS:.*?\]/gi, '').trim() : '';
            const cleanAddress2 = lead?.address2 ? lead.address2.replace(/\s*\[GPS:.*?\]/gi, '').trim() : '';

            if ((!latVal) && lead?.address) {
              const gpsMatch = lead.address.match(/\[GPS:\s*Lat:\s*([\d.-]+),\s*Lon:\s*([\d.-]+)\]/i);
              if (gpsMatch) {
                latVal = gpsMatch[1];
                lngVal = gpsMatch[2];
              }
            }

            const hasGps = !!latVal && !!lngVal;

            return (
              <div className="flex flex-col gap-6 max-w-5xl mx-auto">

                {/* Agency / Shop Details */}
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                  <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><Building2 size={20} className="text-primary"/> Agency / Shop Details</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                    <div>
                      <p className="text-sm text-muted mb-1">Lead Source</p>
                      <p className="font-semibold text-main">{lead?.source?.name || (typeof lead?.source === 'string' ? lead?.source : null) || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Agency Type</p>
                      <p className="font-semibold text-main">{lead?.agencyType || 'N/A'} {lead?.otherAgencyType ? '(' + lead.otherAgencyType + ')' : ''}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Agency / Shop Name</p>
                      <p className="font-semibold text-main">{lead?.companyName || lead?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Phone Number</p>
                      <p className="font-semibold text-main">{lead?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Email Address</p>
                      <p className="font-semibold text-main">{lead?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Address Line 1</p>
                      <p className="font-semibold text-main">{cleanAddress1 || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Address Line 2</p>
                      <p className="font-semibold text-main">{cleanAddress2 || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">City</p>
                      <p className="font-semibold text-main">{lead?.city || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">State</p>
                      <p className="font-semibold text-main">{lead?.state || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted mb-1">Pincode</p>
                      <p className="font-semibold text-main">{lead?.pincode || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Google Map Button (if GPS available) */}
                  {true && latVal && lngVal && (
                    <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center' }}>
                      <a
                        href={`https://www.google.com/maps?q=${latVal},${lngVal}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn"
                        style={{
                          backgroundColor: '#059669',
                          color: 'white',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1.25rem',
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.875rem',
                          textDecoration: 'none'
                        }}
                      >
                        <MapPin size={16} /> Open in Google Maps ↗
                      </a>
                    </div>
                  )}
                </div>

              {/* Contact Details */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><User size={20} className="text-primary"/> Contact Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <p className="text-sm text-muted mb-1">Contact Person</p>
                    <p className="font-semibold text-main">{lead?.contactPerson || lead?.contact || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">Phone Number</p>
                    <p className="font-semibold text-main">{lead?.contactPhone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">Email Address</p>
                    <p className="font-semibold text-main">{lead?.contactEmail || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Product Interest */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><Tag size={20} className="text-primary"/> Product Interest</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <p className="text-sm text-muted mb-1">Interest Type</p>
                    <p className="font-semibold text-main">{lead?.type || lead?.interestType || 'N/A'}</p>
                  </div>
                  {(lead?.type === 'Interested in machine' || lead?.type === 'In Future' || lead?.interestType === 'Interested in machine' || lead?.interestType === 'In Future') && (
                    <>
                      <div>
                        <p className="text-sm text-muted mb-1">Machine Type</p>
                        <p className="font-semibold text-main">{lead?.machineType === 'rental' ? 'Rental Machine' : lead?.machineType === 'new' ? 'New Machine' : lead?.machineType || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted mb-1">Machine Brand</p>
                        <p className="font-semibold text-main">{lead?.machineBrand || lead?.machine?.name || (typeof lead?.machine === 'string' ? lead?.machine : null) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted mb-1">Machine Model</p>
                        <p className="font-semibold text-main">{lead?.machineModel || 'N/A'}</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Tracking & Assignment */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><CheckCircle2 size={20} className="text-primary"/> Tracking & Assignment</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                  <div>
                    <p className="text-sm text-muted mb-1">Status</p>
                    <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', backgroundColor: '#f1f5f9', color: '#475569', fontSize: '0.875rem', fontWeight: '600' }}>
                      {lead?.status?.name || (typeof lead?.status === 'string' ? lead?.status : null) || 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">Customer Type</p>
                    <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            backgroundColor: lead?.customerType === 'Hot' ? '#ffedd5' : lead?.customerType === 'Warm' ? '#fef3c7' : '#e0f2fe',
                            color: lead?.customerType === 'Hot' ? '#ea580c' : lead?.customerType === 'Warm' ? '#d97706' : '#0369a1',
                            fontSize: '0.875rem',
                            fontWeight: '600'
                          }}>
                      {lead?.customerType || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">Assigned Executive</p>
                    <p className="font-semibold text-main flex items-center gap-2">
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        {lead?.assignee ? lead.assignee.charAt(0) : 'U'}
                      </div>
                      {lead?.assignee || 'Unassigned'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted mb-1">Created By</p>
                    <p className="font-semibold text-main">{lead?.createdBy || 'System'}</p>
                  </div>
                </div>
              </div>

              {/* Visiting Card Images */}
              {(lead?.frontCard || lead?.backCard) && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
                  <h3 className="font-bold text-lg mb-6 flex items-center gap-2"><FileText size={20} className="text-primary"/> Visiting Card Images</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {lead?.frontCard && (
                      <div>
                        <p className="text-sm text-muted mb-2">Front Side</p>
                        <div style={{ borderRadius: '8px', border: '1px solid var(--color-border)', padding: '0.5rem', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                          <img src={lead.frontCard} alt="Front Card" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
                        </div>
                      </div>
                    )}
                    {lead?.backCard && (
                      <div>
                        <p className="text-sm text-muted mb-2">Back Side</p>
                        <div style={{ borderRadius: '8px', border: '1px solid var(--color-border)', padding: '0.5rem', backgroundColor: '#f8fafc', display: 'flex', justifyContent: 'center' }}>
                          <img src={lead.backCard} alt="Back Card" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

</div>
            );
          })() : (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-bold text-lg flex items-center gap-2"><Activity size={20} className="text-primary" /> Activity Timeline</h3>

                </div>
                {(() => {
                  const timelineEvents = [];
                  if (localFollowups) {
                    localFollowups.forEach(f => {
                      const notes = f.notes || '';
                      const clean = f.cleanNotes || '';


                      // 1. Every row was originally a scheduled action (or an immediate action). 
                      // However, if this row was spawned precisely when another row was rescheduled, it's redundant to show a "Scheduled" log 
                      // precisely at the same millisecond as the "Rescheduled" log.
                      const wasSpawned = localFollowups.find(otherF => {
                        if (otherF.id === f.id) return false;
                        if (f.notes?.includes('Visit Started') || f.notes?.includes('[Complete') || f.notes?.includes('[Cancel') || f.notes?.includes('[Status Change')) return false;
                        const isModified = otherF.notes?.includes('[Reschedule') || otherF.notes?.includes('[Edit');
                        return isModified && otherF.updatedAt && Math.abs(new Date(otherF.updatedAt).getTime() - new Date(f.createdAt).getTime()) < 5000;
                      });
                      
                      if (!wasSpawned) {
                        timelineEvents.push({ type: 'followup', data: f, isModification: false });
                      }
                      
                      // 2. If it was modified later (rescheduled, edited, completed, etc.), push a modification event!
                      const hasPrefix = f.notes?.match(/^\[.*?\]/);
                      if (hasPrefix && !f.notes?.startsWith('[Closed]') && f.updatedAt && Math.abs(new Date(f.updatedAt).getTime() - new Date(f.createdAt).getTime()) > 5000) {
                        timelineEvents.push({ type: 'followup', data: f, isModification: true });
                      }
                    });
                  }
                  
                  // Merge visits from LeadVisit table if not already in followups
                  if (visits && visits.length > 0) {
                    visits.forEach(v => {
                      const checkInTime = new Date(v.checkIn).getTime();
                      const exists = localFollowups && localFollowups.some(f => {
                        const fTime = new Date(f.createdAt || f.date).getTime();
                        const isVisitStart = f.type === 'Visit Started' || f.type === 'Visit' || (f.notes || '').includes('Visit Started') || (f.notes || '').toLowerCase().includes('start visit');
                        return isVisitStart && Math.abs(fTime - checkInTime) < 300000;
                      });
                      if (!exists) {
                        timelineEvents.push({
                          type: 'followup',
                          data: {
                            id: `visit-${v.id}`,
                            date: v.checkIn,
                            createdAt: v.checkIn,
                            notes: v.notes ? `Visit Started: ${v.notes}` : `Visit Started`,
                            type: 'Visit Started',
                            latitude: v.latitude,
                            longitude: v.longitude,
                            createdBy: v.createdBy || 'System'
                          },
                          isModification: false
                        });
                      }
                      if (v.checkOut) {
                        const checkOutTime = new Date(v.checkOut).getTime();
                        const checkoutExists = localFollowups && localFollowups.some(f => {
                          const fTime = new Date(f.createdAt || f.date).getTime();
                          const isVisitComplete = f.type === 'Visit Completed' || (f.notes || '').includes('Visit Completed') || (f.notes || '').toLowerCase().includes('complete visit');
                          return isVisitComplete && Math.abs(fTime - checkOutTime) < 300000;
                        });
                        if (!checkoutExists) {
                          timelineEvents.push({
                            type: 'followup',
                            data: {
                              id: `visit-checkout-${v.id}`,
                              date: v.checkOut,
                              createdAt: v.checkOut,
                              notes: `Visit Completed`,
                              type: 'Visit Completed',
                              latitude: v.latitude,
                              longitude: v.longitude,
                              createdBy: v.createdBy || 'System'
                            },
                            isModification: false
                          });
                        }
                      }
                    });
                  }

                  timelineEvents.push({ type: 'notes', data: lead?.notes || '', createdAt: lead?.createdAt });

                  timelineEvents.sort((a, b) => {
                    const getEventDate = (e) => {
                      if (e.type === 'notes') return new Date(e.createdAt).getTime();
                      if (e.type === 'followup' && e.isModification) return new Date(e.data.updatedAt).getTime();
                      return new Date(e.data.createdAt).getTime();
                    };
                    return getEventDate(b) - getEventDate(a);
                  });

                  if (timelineEvents.length === 0) {
                    return <p className="text-muted text-sm italic bg-slate-50 p-6 rounded-lg border border-dashed border-slate-200 text-center">No timeline activity yet.</p>;
                  }

                  return (
                    <div className="flex flex-col gap-2" style={{ paddingLeft: '1rem', position: 'relative' }}>
                      {timelineEvents.map((event, idx) => {
                        const isModifiedAction = event.type === 'followup' && event.isModification;
                        const actionDoer = isModifiedAction ? (lead?.assignee || event.data.createdBy) : event?.data?.createdBy;
                        
                        return (
                        <div key={idx} className="flex gap-4 relative z-10 mb-2">
                          {idx !== timelineEvents.length - 1 && (
                            <div style={{ position: 'absolute', left: '1.125rem', top: '2.25rem', bottom: '-1rem', width: '2px', backgroundColor: 'var(--color-border)', zIndex: -1 }}></div>
                          )}
                          
                          {event.type === 'followup' ? (
                            <>
                              {(() => {
                                const n = event.data.notes || '';
                                const t = event.data.type || '';
                                let IconCmp = Calendar;
                                if (t === 'Visit Started' || n.includes('Visit Started')) IconCmp = Activity;
                                else if (t.includes('Complete') || n.includes('Complete') || n.includes('Closed') || n.includes('Won')) IconCmp = CheckCircle2;
                                else if (t.includes('Schedule') || n.includes('Schedule') || n.includes('Reschedule')) IconCmp = Calendar;
                                else if (t.includes('Edit') || n.includes('Edit')) IconCmp = Edit2;
                                else if (t.includes('Assign') || n.includes('Assign')) IconCmp = User;
                                else if (t.includes('Cancel') || n.includes('Cancel') || n.includes('Lost')) IconCmp = X;
                                else if (t === 'Created Lead') IconCmp = Activity;
                                return (
                                  <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <IconCmp size={16} className="text-success" />
                                  </div>
                                );
                              })()}
                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex-1 relative group">
                                {currentUser?.permissions?.includes('followups:write') && idx === timelineEvents.findIndex(e => e.type === 'followup') && event.data.type !== 'System' && !(event.data.notes || '').startsWith('Visit Started') && !(event.data.notes || '').match(/^\[.*?\]/) && (
                                  <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    
                                  <button onClick={() => setDeleteFollowupId(event.data.id)} className="text-muted hover:text-danger transition-colors" title="Delete Follow-up">
                                    <Trash2 size={16} />
                                  </button>
                                  </div>
                                )}
                                
                                {(() => {
                                  const n = event.data.notes || '';
                                  const t = event.data.type || '';
                                  const dt = event.data.displayTitle;

                                  let itemTitle = 'Follow-up Scheduled';

                                  if (n.includes('[Status Change:') || n.includes('[Status Change]')) {
                                    const match = n.match(/(?:to|Status changed to)\s+(.*?)(?:]|\.|\n|$)/i) || n.match(/\[Status Change:\s*(.*?)\]/i);
                                    if (match && match[1]) {
                                      itemTitle = `Status Update to ${match[1].trim()}`;
                                    } else {
                                      itemTitle = 'Status Update';
                                    }
                                  } else if (n.includes('[Assigned]') || t === 'Assigned') {
                                    itemTitle = 'Lead Reassigned';
                                  } else if (t === 'Visit Started' || n.startsWith('Visit Started') || n.includes('[Start Visit]')) {
                                    itemTitle = 'Visit Started';
                                  } else if (t === 'Visit Completed' || n.includes('Visit Completed') || n.includes('[Complete Visit]')) {
                                    itemTitle = 'Visit Completed';
                                  } else if (t === 'Visit Cancelled' || t === 'Follow-up Cancelled' || n.includes('Visit Cancelled') || n.includes('Follow-up Cancelled') || n.includes('System Auto-Cancelled') || n.includes('[Cancel Visit]') || n.includes('[Cancel]')) {
                                    itemTitle = 'Follow-up Cancelled';
                                  } else if (t === 'Follow-up Rescheduled' || n.includes('[Reschedule]') || n.toLowerCase().includes('reschedule')) {
                                    itemTitle = 'Follow-up Rescheduled';
                                  } else if (n.startsWith('[Closed]') || t === 'Follow-up Closed') {
                                    itemTitle = 'Follow-up Scheduled';
                                  } else if (dt && dt !== 'Follow-up Closed' && dt !== 'System') {
                                    itemTitle = dt;
                                  } else if (t && !['System', 'followup', 'Visit', 'Follow-up Closed'].includes(t)) {
                                    itemTitle = t;
                                  } else if (n.includes('[Edit]')) {
                                    itemTitle = 'Follow-up Edited';
                                  }

                                  const isScheduledType = itemTitle === 'Follow-up Scheduled' || itemTitle === 'Follow-up Rescheduled' || t === 'Follow-up Scheduled' || t === 'Follow-up Rescheduled';
                                  const showScheduledFor = !event.isModification && isScheduledType && event.data.date;

                                  return (
                                    <>
                                      <p className="font-bold text-main">{itemTitle}</p>
                                      <p className="text-sm text-muted mt-1">
                                        {actionDoer ? `By ${actionDoer} on ` : 'On '}
                                        {formatDate(isModifiedAction ? event.data.updatedAt : event.data.createdAt)}
                                        {showScheduledFor ? ` • Scheduled for: ${formatDate(event.data.date)}` : ''}
                                      </p>
                                    </>
                                  );
                                })()}
                                
                                {(() => {
                                  // Do not hide the original scheduled events, let them display as "Lead Scheduled"
                                  /* if (!event.isModification && !event.isCombinedMod && event.data.type !== 'System' && (event.data.notes?.includes('[Complete') || event.data.notes?.includes('[Closed]') || event.data.notes?.includes('[Cancel') || event.data.notes?.includes('[Reschedule') || event.data.notes?.includes('[Edit'))) {
                                    return null;
                                  } */
  
                                  let rawNotes = event.isCombinedMod ? (event.parentNotes || '') : (event.data.notes || '');
                                  let matchedReason = null;
                                  
                                  // Parse reason if present
                                  const match = rawNotes.match(/^\[.*?\] Reason:\s(.*?)\.\s?(.*)$/is);
                                  // The original scheduled event (isModification: false) shouldn't show the notes of its FUTURE modification!
                                  const isInstant2 = !event.data.updatedAt || Math.abs(new Date(event.data.updatedAt).getTime() - new Date(event.data.createdAt).getTime()) <= 5000;
                                  if (!event.isModification && !isInstant2 && rawNotes.match(/^\[.*?\]/) && !rawNotes.includes('[Closed] Visit Started')) {
                                    rawNotes = '';
                                  }
                                  if (match) {
                                    const prefixMatch = event.isCombinedMod ? (event.parentNotes || '').match(/^\[(.*?)\]/) : (event.data.notes || '').match(/^\[(.*?)\]/);
                                    if (prefixMatch && ['Complete', 'Completed', 'Closed', 'Lost'].includes(prefixMatch[1])) {
                                      matchedReason = null;
                                      rawNotes = match[2];
                                    } else {
                                      matchedReason = match[1];
                                      rawNotes = match[2];
                                    }
                                  } else {
                                    let cleanNotes = rawNotes.replace(/^(?:\[.*?\]\s*)+/, '').replace(/^Visit Started:\s*(\(Started before schedule\)\s*)?/, '').trim();
                                    rawNotes = cleanNotes;
                                  }
                                  
                                  if (!matchedReason && !rawNotes) return null;
                                  
                                  return (
                                    <div className="mt-2 flex flex-col gap-2">
                                      {matchedReason && (
                                        <div>
                                          <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            backgroundColor: 'var(--color-primary-light)',
                                            color: 'var(--color-primary)'
                                          }}>
                                            Reason: {matchedReason}
                                          </span>
                                        </div>
                                      )}
                                      {rawNotes && rawNotes.toLowerCase() !== 'scheduled' && rawNotes.toLowerCase() !== 'rescheduled' && (
                                        <div style={{
                                          fontSize: '14px',
                                          color: 'var(--color-text-muted)',
                                          backgroundColor: 'white',
                                          border: '1px solid var(--color-border)',
                                          padding: '0.5rem',
                                          borderRadius: '8px',
                                          whiteSpace: 'pre-wrap'
                                        }}>
                                          {rawNotes}
                                        </div>
                                      )}
                                      {event.data?.latitude && event.data?.longitude && (
                                        <div style={{ marginTop: '0.35rem' }}>
                                          <a
                                            href={`https://www.google.com/maps?q=${event.data.latitude},${event.data.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '0.35rem',
                                              padding: '0.35rem 0.75rem',
                                              borderRadius: '6px',
                                              fontSize: '0.75rem',
                                              fontWeight: '600',
                                              backgroundColor: '#059669',
                                              color: 'white',
                                              textDecoration: 'none'
                                            }}
                                          >
                                            <MapPin size={14} /> Open Location in Google Maps ↗
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ width: '2.25rem', height: '2.25rem', borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <User size={16} className="text-success" />
                              </div>
                              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex-1 relative group">
                                <p className="font-bold text-main">Created Lead</p>
                                <p className="text-sm text-muted mt-1">Added during creation {lead?.createdBy && lead.createdBy !== 'Unknown' ? `by ${lead.createdBy} ` : ''}on {formatDate(event.createdAt)}</p>
                                {event.data && (
                                  <div className="mt-2" style={{
                                    fontSize: '14px',
                                    color: 'var(--color-text-muted)',
                                    backgroundColor: 'white',
                                    border: '1px solid var(--color-border)',
                                    padding: '0.5rem',
                                    borderRadius: '8px',
                                    whiteSpace: 'pre-wrap'
                                  }}>
                                    {event.data}
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Delete Modal */}
      {deleteFollowupId && (
        <div className="modal-backdrop fade-in" style={{ zIndex: 1000 }}>
          <div className="modal-content" style={{ width: '100%', maxWidth: '400px', padding: '2rem' }}>
            <h3 className="text-xl font-bold mb-2">Delete Follow-up</h3>
            <p className="text-muted mb-6">Are you sure you want to delete this follow-up? This action cannot be undone.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button className="btn" onClick={() => setDeleteFollowupId(null)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn" onClick={confirmDelete} style={{ backgroundColor: 'var(--color-danger)', color: 'white', border: 'none' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Edit Modal */}
      {editFollowupData && (
        <div className="modal-backdrop fade-in" style={{ zIndex: 1000 }}>
          <div className="modal-content" style={{ width: '100%', maxWidth: '500px', padding: '2.5rem' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Edit Follow-up</h3>
              <button onClick={() => setEditFollowupData(null)} style={{ padding: '8px', borderRadius: '50%', backgroundColor: '#f1f5f9', color: 'var(--color-text-muted)' }}><X size={20} /></button>
            </div>
            
            {(() => {
              const originalPrefixMatch = editFollowupData.notes?.match(/^\[(.*?)\]/);
              const canEditDate = !originalPrefixMatch || ['Schedule', 'Scheduled', 'Reschedule', 'Rescheduled'].includes(originalPrefixMatch[1]);
              
              const getTimeDate = (timeStr) => {
                if (!timeStr) return null;
                const [h, m] = timeStr.split(':');
                const d = new Date();
                d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
                return d;
              };
              
              return (
            <>
            <div className="flex flex-col gap-4 mb-8">
              {canEditDate && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted" style={{ color: '#0369a1' }}>Follow-up Date <span style={{color: 'var(--color-danger)'}}>*</span></label>
                    <DatePicker
                      selected={editDate}
                      onChange={(date) => setEditDate(date)}
                      className="input-field w-full"
                      wrapperClassName="w-full"
                      style={{ backgroundColor: 'white', width: '100%' }}
                      placeholderText="Select date..."
                      dateFormat="dd/MM/yyyy"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold mb-2 block text-muted" style={{ color: '#0369a1' }}>Follow-up Time <span style={{color: 'var(--color-danger)'}}>*</span></label>
                    <DatePicker
                      wrapperClassName="w-full"
                      selected={getTimeDate(editTime)}
                      onChange={(date) => {
                        if (date) {
                          const hours = date.getHours().toString().padStart(2, '0');
                          const minutes = date.getMinutes().toString().padStart(2, '0');
                          setEditTime(`${hours}:${minutes}`);
                        } else {
                          setEditTime('');
                        }
                      }}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="hh:mm aa"
                      className="input-field w-full"
                      style={{ backgroundColor: 'white', width: '100%' }}
                      placeholderText="Select time..."
                      required
                      minTime={
                        editDate && new Date(editDate).setHours(0,0,0,0) === new Date().setHours(0,0,0,0)
                          ? new Date()
                          : new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      maxTime={new Date(new Date().setHours(23, 59, 59, 999))}
                    />
                  </div>
                </div>
              )}}
              <div>
                <label className="text-sm font-semibold mb-2 block text-muted">Notes</label>
                <textarea 
                  className="input-field" 
                  rows="4" 
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Update your notes here..."
                  style={{ width: '100%', borderRadius: '12px' }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button className="btn" onClick={() => setEditFollowupData(null)} style={{ border: '1px solid var(--color-border)', backgroundColor: 'white' }}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmEdit}>Save Changes</button>
            </div>
            </>
            );
            })()}
          </div>
        </div>
      )}

      {showCheckInModal && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }}>
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem' }}>
            <h3 className="text-xl font-bold mb-4">Check-In to Site Visit</h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Check-In Note</label>
              <textarea className="input-field w-full" rows="3" placeholder="Add any details about this visit..." value={checkInNote} onChange={e => setCheckInNote(e.target.value)}></textarea>
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn" onClick={() => setShowCheckInModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCheckIn}>Confirm Check-In</button>
            </div>
          </div>
        </div>
      )}
      {showCheckOutModal && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }}>
          <div className="modal-content fade-in" style={{ maxWidth: '400px', padding: '2rem' }}>
            <h3 className="text-xl font-bold mb-4">Confirm Check-Out</h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-2">Check-Out Time (Optional, defaults to now)</label>
              <input type="datetime-local" className="input-field w-full" value={checkOutTime} onChange={e => setCheckOutTime(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <button className="btn" onClick={() => setShowCheckOutModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCheckOut}>Confirm Check-Out</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


export default LeadProfile;
